import React from 'react';
import ReactHTMLTableToExcel from 'react-html-table-to-excel';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import calculateVatRate from 'bl/payment/calculateVatRate.bl';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import Tooltip from 'components/generic/Tooltip.component';
import CurrencySign from 'components/price/CurrencySign.component';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import truncateString from 'utils/strings/truncateString.util';
import C from 'utils/values/constant.util';
import fixed from 'utils/values/fixed.util';

import CN from './StaffPerformanceTable.module.scss';

const TOOLTIP_W_DAYS = 'Number of days the business is open and at least 1 hour is worked, ie not blocked with time off';
const TOOLTIP_OCCUPANCY = [
  'Please note, stylists with the following job titles will be excluded from the total occupancy calculation:',
  ' Apprentice, Graduate, Assistant, Front of house, Reception',
].join(S.space);

const TOOLTIP_APPOINTMENT = [
  'Number of appointments in the diary for this date range (excluding any cancelled bookings or no-shows).',
  'Appointments with 2 services count as 1. The same client with 2 separate appointments counts as 2.',
].join(S.space);

const TOOLTIP_TOTAL_BOOKING = [
  'Value of total appointments in the period, where value is the sum of service prices for these appointments.',
  'This figure is NOT based on the value checked out so excludes any discounts/retail - for this figure please see Revenue tab',
].join(S.space);

const TOOLTIP_AVR_BOOKING = [
  'Total booking value divided by Total number of appointments.',
  'This is different to Average Bill Value which is based on checked out appointments (see Revenue tab)',
].join(S.space);

const TOOLTIP_N_A = ['If you are seeing this, ', 'it means that this person had appointments booked over a time period where they were not working/had time off'].join(S.space);

const tooltipContents = C([
  { id: 1, text: TOOLTIP_W_DAYS, label: 'Days Worked' },
  { id: 2, text: TOOLTIP_OCCUPANCY, label: 'Occupancy' },
  { id: 3, text: TOOLTIP_APPOINTMENT, label: 'Total appointments' },
  { id: 4, text: TOOLTIP_TOTAL_BOOKING, label: 'Total booking value' },
  { id: 5, text: TOOLTIP_AVR_BOOKING, label: 'Average booking value' },
]);

const StaffPerformanceTable = ({ appointmentsReport, salonHoursReport, startDate, endDate, salonVatRate, stylistIDInCalendarOrder }) => {
  const { isDarkMode } = useTheme();
  const salonId = useCurrentSalonId();

  const marketingLink = linkTo({ pattern: LINK.marketing, params: { salonId }, fault: '#' });

  const stylistGroupAppointments = appointmentsReport.lastRequestResponse.stylist_group || {};
  const stylistGroupHours = salonHoursReport.lastRequestResponse.stylist_group || {};

  const tableHeader = (
    <tr>
      <th className={CN.firstCell}>Staff</th>
      {tooltipContents.map(({ text, label }, index) => {
        const isLast = index === tooltipContents.length - 1;
        return (
          <th key={createNodeKey({ index })}>
            <div className={CN.tableHeaderText}>
              <div className={CN.labelSpace}>{label}</div>
              <Tooltip content={text} variant={isLast ? 'quaternaryLeftToBottom' : 'quaternaryBottom'} width="max">
                <div className={CN.iconTooltip}>
                  <Icon variant="info" width="sm" />
                </div>
              </Tooltip>
            </div>
          </th>
        );
      })}
    </tr>
  );

  // reorder stylist like in calendar
  const tableRows = stylistIDInCalendarOrder
    .filter((stylist) => stylistGroupAppointments[stylist.id] && stylistGroupHours[stylist.id])
    .map((stylist) => {
      const stylistId = stylist.id;
      const appointment = stylistGroupAppointments[stylistId];
      const hours = stylistGroupHours[stylistId];

      const daysWorked = hours ? hours.actual_days : 0;
      const busyMinutes = hours ? hours.busy_mins : 0;
      const actualMinutes = hours ? hours.actual_mins : 0;

      // occupancy is calculated as busy_mins divided by actual_mins expressed as a %
      let occupancyPercentage = hours ? ((busyMinutes / actualMinutes) * 100).toFixed() : 0;
      if (actualMinutes !== 0 && !isNaN(busyMinutes) && !isNaN(actualMinutes)) {
        occupancyPercentage = ((busyMinutes / actualMinutes) * 100).toFixed();
      }
      const occupancyTooltip =
        actualMinutes === 0 ? (
          <div className={CN.flex}>
            <Tooltip content={TOOLTIP_N_A} variant="quaternaryBottom" width="max">
              <div className={CN.flex}>
                <div className={CN.space}>N/A </div>
                <Icon variant="info" width="sm" />
              </div>
            </Tooltip>
          </div>
        ) : (
          `${occupancyPercentage}%`
        );

      const averageBookingValue = fixed(appointment.total / appointment.count);
      // calculate value after vat
      const totalAfterVat = calculateVatRate(appointment.total, salonVatRate);
      const averageBookingValueAfterVat = calculateVatRate(averageBookingValue, salonVatRate);

      return (
        <tr key={stylistId}>
          <td className={CN.firstCell}>{truncateString({ value: appointment.name, max: 24, suffix: '...' })}</td>
          <td>{daysWorked}</td>
          <td>{occupancyTooltip}</td>
          <td>{appointment.count}</td>
          <td>
            <CurrencySign value={totalAfterVat} />
          </td>
          <td>
            <CurrencySign value={averageBookingValueAfterVat} />
          </td>
        </tr>
      );
    });

  // calculate totals in footer
  const salonDaysWorkedTotal = Object.values(stylistGroupHours).reduce((total, stylistHours) => (stylistHours && stylistHours.actual_days !== 0 ? total + stylistHours.actual_days : total), 0);

  const { salon_occupancy_mins, total_salon_man_mins, total_time_off_mins } = salonHoursReport.lastRequestResponse;
  const salonOccupancyTotal = (salon_occupancy_mins * 100) / (total_salon_man_mins - total_time_off_mins);

  const salonAppointmentsTotal = Object.values(stylistGroupAppointments).reduce((total, stylist) => total + stylist.count, 0);

  const salonTotalValue = Object.values(stylistGroupAppointments).reduce((total, stylist) => total + stylist.total, 0);

  const salonAverageBookingValue = salonAppointmentsTotal ? fixed(salonTotalValue / salonAppointmentsTotal) : 0;
  // calculate value after vat in footer
  const salonTotalValueAfterVat = calculateVatRate(salonTotalValue, salonVatRate);
  const salonAverageBookingValueAfterVat = calculateVatRate(salonAverageBookingValue, salonVatRate);

  return (
    <div
      data-bem="StaffPerformanceTable"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div className={CN.panelHeading}>
        <div className={CN.title}>Staff performance</div>
        <div className={CN.buttonsContainer}>
          <NavLink to={marketingLink}>
            <Button variant="primaryForce" width="small" onClick={alwaysNoop}>
              <span className={CN.buttonFont}>GET MORE BOOKINGS!</span>
            </Button>
          </NavLink>

          <ReactHTMLTableToExcel className={CN.buttonPrimary} table="StaffPerformanceTable" filename="Staff performance" sheet="tablexls" buttonText="DOWNLOAD TO EXCEL" />
        </div>
      </div>
      <div className={CN.timeContainer}>
        <div>
          From <span className={CN.bold}>{startDate}</span>
        </div>
        <div>
          <span className={CN.gap}>to</span> <span className={CN.bold}>{endDate}</span>
        </div>
      </div>
      <div className={CN.scrollContainer}>
        <table className={CN.table} id="StaffPerformanceTable">
          <thead>{tableHeader}</thead>
          <tbody>{tableRows}</tbody>
          <tfoot>
            <tr>
              <td className={CN.firstCell}>
                <span className={CN.bold}>Salon Total</span>
              </td>
              <td>
                <span className={CN.bold}>{salonDaysWorkedTotal}</span>
              </td>
              <td>
                <span className={CN.bold}>{salonOccupancyTotal.toFixed()}%</span>
              </td>
              <td>
                <span className={CN.bold}>{salonAppointmentsTotal}</span>
              </td>
              <td>
                <span className={CN.bold}>
                  <CurrencySign value={salonTotalValueAfterVat} />
                </span>
              </td>
              <td>
                <span className={CN.bold}>
                  <CurrencySign value={salonAverageBookingValueAfterVat} />
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  appointmentsReport: state?.reports?.appointmentsReport,
  salonHoursReport: state?.reports?.salonHoursReport,
  salonVatRate: state?.reports?.salonVatRate,
  stylistIDInCalendarOrder: state?.reports?.stylistIDInCalendarOrder,
});

const StaffPerformanceTableHoc = connect(mapStateToProps)(StaffPerformanceTable);

export default StaffPerformanceTableHoc;
